import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { api } from '../api/client';
import { 
  Package, 
  Building2, 
  ChevronDown, 
  Search, 
  Bell, 
  Sun, 
  Moon, 
  User,
  Settings,
  LogOut,
  Package2
} from 'lucide-react';
import AlertToggle from './ui/AlertToggle';
import AlertNotificationBadge from './ui/AlertNotificationBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function EnterpriseHeader({ 
  subscriptionData, 
  notificationCount = 0 
}) {
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [companySettings, setCompanySettings] = useState(null);

  // Fetch company settings for logo
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await api.get('/api/v1/company/settings');
        
        if (response.success) {
          setCompanySettings(response.settings);
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
      }
    };

    fetchCompanySettings();
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-neutral-200 dark:border-neutral-700
                     bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl shadow-sm">
      <div className="px-6 py-2">
        <div className="flex items-center justify-between">
          {/* Logo & Branding - Clickable */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer group"
            >
              {/* Logo - Show company logo if exists */}
              {companySettings?.logo_url ? (
                <img 
                  src={companySettings.logo_url} 
                  alt="Company Logo" 
                  className="w-7 h-7 rounded-lg object-cover shadow-sm"
                />
              ) : (
                <div className="w-7 h-7 rounded-lg bg-enterprise-primary-500 flex items-center justify-center shadow-sm
                              group-hover:bg-enterprise-primary-600 transition-colors">
                  <span className="text-white font-bold text-sm">
                    {companySettings?.company_name?.charAt(0)?.toUpperCase() || 'Z'}
                  </span>
                </div>
              )}
              
              {/* Company Name */}
              <div>
                <h1 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 
                             group-hover:text-enterprise-primary-600 dark:group-hover:text-enterprise-primary-400 
                             transition-colors leading-tight">
                  {companySettings?.company_name || 'ZuperBooks'}
                </h1>
                <p className="text-[10px] text-neutral-500 dark:text-neutral-400 font-medium leading-tight">
                  Enterprise Edition
                </p>
              </div>
            </button>
            
            {/* Tenant Selector */}
            <TenantSelector />
          </div>
          
          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Global Search */}
            <div className="hidden md:flex relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search products, SKUs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 text-sm border border-neutral-200 dark:border-neutral-600
                         rounded-lg bg-neutral-50 dark:bg-neutral-800 
                         text-neutral-900 dark:text-neutral-100
                         placeholder-neutral-500 dark:placeholder-neutral-400
                         focus:outline-none focus:ring-2 focus:ring-enterprise-primary-500 
                         focus:border-transparent transition-all"
              />
            </div>
            
            {/* Alert Notifications */}
            <AlertNotificationBadge />
            
            {/* Alert Toggle */}
            <AlertToggle />
            
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="p-2 h-auto"
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 text-neutral-600" />
              ) : (
                <Sun className="w-5 h-5 text-neutral-400" />
              )}
            </Button>
            
            {/* Subscription Badge */}
            <Badge className="bg-enterprise-success-50 text-enterprise-success-700 border-enterprise-success-200
                            dark:bg-enterprise-success-900/20 dark:text-enterprise-success-400 dark:border-enterprise-success-800">
              {subscriptionData?.plan?.name || 'Pro'} Plan
            </Badge>
            
            {/* User Menu */}
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  );
}

// Tenant Selector Component
function TenantSelector() {
  const { user } = useAuth();
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await api.get('/api/v1/company/settings');
        
        if (response.success) {
          setCompanySettings(response.settings);
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchCompanySettings();
    }
  }, [user]);

  const currentTenant = {
    name: companySettings?.company_name || user?.company_name || 'Demo Company',
    logo: companySettings?.logo_url,
    domain: 'zuperbooks.com'
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center gap-2 px-3 py-2 h-auto
                   text-neutral-700 dark:text-neutral-300
                   hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          {currentTenant.logo ? (
            <img 
              src={currentTenant.logo} 
              alt="Company logo" 
              className="w-5 h-5 rounded object-cover"
            />
          ) : (
            <Building2 className="w-4 h-4" />
          )}
          <span className="text-sm font-medium">{currentTenant.name}</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>
          <div className="flex items-center gap-2">
            {currentTenant.logo ? (
              <img 
                src={currentTenant.logo} 
                alt="Company logo" 
                className="w-8 h-8 rounded object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <span className="text-sm font-bold text-gray-600 dark:text-gray-300">
                  {currentTenant.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <div className="font-medium">{currentTenant.name}</div>
              <div className="text-xs text-neutral-500">{currentTenant.domain}</div>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <Settings className="w-4 h-4 mr-2" />
          Tenant Settings
        </DropdownMenuItem>
        <DropdownMenuItem>
          <Package className="w-4 h-4 mr-2" />
          Switch Tenant
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// User Menu Component
function UserMenu() {
  const { user, logout, role } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleLogout = async () => {
    try {
      setIsOpen(false);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!user) {
    return null;
  }
  
  // Get initials from name
  const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg 
                   hover:bg-neutral-100 dark:hover:bg-neutral-800 
                   transition-colors"
      >
        {user.picture ? (
          <img 
            src={user.picture} 
            alt={user.name}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-600 dark:bg-blue-500
                        flex items-center justify-center text-white font-medium text-sm">
            {initials}
          </div>
        )}
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {user.name}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            {user.email}
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-neutral-500" />
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 
                      bg-white dark:bg-gray-800 
                      rounded-lg shadow-xl 
                      border border-gray-200 dark:border-gray-700 
                      z-50 overflow-hidden">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-600 dark:bg-blue-500
                              flex items-center justify-center text-white font-semibold text-base">
                  {initials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user.email}
                </p>
              </div>
            </div>
            {user.company_name && (
              <div className="flex items-center gap-1">
                <Building2 className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium truncate">
                  {user.company_name}
                </p>
              </div>
            )}
            {role && (
              <div className="mt-1">
                <Badge variant="secondary" className="text-xs">
                  {role}
                </Badge>
              </div>
            )}
          </div>
          
          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/profile');
              }}
              className="w-full px-4 py-2 text-left text-sm 
                       text-gray-700 dark:text-gray-300 
                       hover:bg-gray-100 dark:hover:bg-gray-700 
                       flex items-center gap-2 transition-colors"
            >
              <Settings className="w-4 h-4" />
              Profile Settings
            </button>
            
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/preferences');
              }}
              className="w-full px-4 py-2 text-left text-sm 
                       text-gray-700 dark:text-gray-300 
                       hover:bg-gray-100 dark:hover:bg-gray-700 
                       flex items-center gap-2 transition-colors"
            >
              <User className="w-4 h-4" />
              Preferences
            </button>
            
            <hr className="my-1 border-gray-200 dark:border-gray-700" />
            
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm 
                       text-red-600 dark:text-red-400 
                       hover:bg-gray-100 dark:hover:bg-gray-700 
                       flex items-center gap-2 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnterpriseHeader;